import {
  PARAM_TYPE_ARRAY,
  PARAM_TYPE_BOOLEAN,
  PARAM_TYPE_NUMBER,
  PARAM_TYPE_STRING,
} from "constant/application";
import { PropAppParamV3Schema, PropFormikField } from "constant/prop-types";
import { ErrorMessage } from "formik";
import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";
import PropTypes from "prop-types";
import React from "react";
import { Button, FormGroup, FormText } from "react-bootstrap";
import { List } from "ui/list";

import { BooleanField } from "./boolean-field";
import { FieldLabel } from "./field-label";
import { FormInput } from "./form-input";
import { NumberField } from "./number-field";
import Overrides from "./overrides";
import { SelectField } from "./select-field";
import { StringField } from "./string-field";
import ParamUtils, { PRIORITY } from "./utils";

function inputFieldFactory(dataType, fieldProps) {
  const { schema } = fieldProps;
  if (dataType === PARAM_TYPE_ARRAY) {
    return <SelectField {...fieldProps} isMulti={true} />;
  }

  if (dataType === PARAM_TYPE_BOOLEAN) {
    return <BooleanField {...fieldProps} uncheckIcon="" />;
  }

  if (dataType === PARAM_TYPE_NUMBER) {
    return <NumberField {...fieldProps} />;
  }

  if (dataType === PARAM_TYPE_STRING) {
    if (schema.options) {
      return <SelectField {...fieldProps} isMulti={false} />;
    }
    return <StringField {...fieldProps} />;
  }

  return <StringField {...fieldProps} />;
}

class ParamFormField extends React.PureComponent {
  static get propTypes() {
    return {
      deviceParam: PropTypes.object,
      errors: PropTypes.array,
      field: PropFormikField.isRequired,
      form: PropTypes.object.isRequired,
      network: PropTypes.object.isRequired,
      networkParams: PropTypes.arrayOf(PropTypes.object).isRequired,
      onOverride: PropTypes.func,
      onRemove: PropTypes.func,
      priority: PropTypes.oneOf([PRIORITY.DEVICE, PRIORITY.NETWORK]).isRequired,
      readOnly: PropTypes.bool,
      schema: PropAppParamV3Schema.isRequired,
    };
  }

  static get defaultProps() {
    return {
      onOverride: noop,
      readOnly: false,
    };
  }

  constructor(props) {
    super(props);
    const { deviceParam, networkParams, priority, schema } = props;
    const { name, default_value: defaultValue } = schema;

    const networkParam = ParamUtils.getNetworkLevelParam(name, networkParams);
    const priorities = ParamUtils.buildPriorities(
      defaultValue,
      networkParam,
      deviceParam,
    );
    const hasOverride = Overrides.hasOverride(priority, priorities);
    const isOverriding = Overrides.isOverriding(priority, priorities);

    this.state = {
      hasOverride,
      isOverriding,
    };

    if (this.state.hasOverride) {
      props.onOverride(this.state.isOverriding);
    }
  }

  _handleChange(name, value) {
    this.props.form.setFieldValue(name, value);
  }

  render() {
    const {
      errors,
      field,
      network,
      networkParams,
      onOverride,
      onRemove,
      priority,
      readOnly,
      schema,
    } = this.props;
    const { hasOverride, isOverriding } = this.state;
    const { data_type: dataType, required: isRequired } = schema;
    const handleChange = this._handleChange.bind(this, field.name);

    const networkLevel = ParamUtils.getNetworkLevelParam(
      field.name,
      networkParams,
    );
    const hasNetworkLevel = Boolean(networkLevel);
    const hasDefaultValue = ParamUtils.hasDefaultValue(schema);
    const hasErrors = (errors || []).length > 0;

    const inputButton = (
      <Button
        data-qa="Remove.Button"
        variant="danger"
        disabled={readOnly}
        onClick={onRemove}
        title="Click to unset this parameter."
      >
        <i className="fa fa-minus" />
      </Button>
    );

    const fieldProps = {
      field,
      handleChange,
      isRequired,
      schema,
      readOnly: readOnly || (hasOverride && !isOverriding),
    };
    const inputField = inputFieldFactory(dataType, fieldProps);

    return (
      <FormGroup className={!isEmpty(errors) ? "has-error" : ""}>
        <FieldLabel priority={priority} schema={schema} />
        <FormInput
          data-qa={field.name}
          hasOverride={hasOverride}
          inputButton={inputButton}
          isRequired={isRequired}
        >
          {inputField}
        </FormInput>
        {hasOverride && (
          <Overrides.Toggle
            defaultValue={isOverriding}
            disabled={readOnly}
            onChange={(value) => {
              this.setState({ isOverriding: value });
              onOverride(value);
            }}
          >
            <Overrides.Label
              networkId={network.id}
              hasDefaultValue={hasDefaultValue}
              hasNetworkLevel={hasNetworkLevel}
              priority={priority}
            />
          </Overrides.Toggle>
        )}
        {hasErrors && (
          <FormText>
            <List className="list-compact" items={errors} />
          </FormText>
        )}
        {!hasErrors && schema.description && (
          <FormText>{schema.description}</FormText>
        )}
        <ErrorMessage name={field.name} component="div" />
      </FormGroup>
    );
  }
}

export { inputFieldFactory, ParamFormField };
